import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import dayjs from "dayjs";
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useAppSelector } from '../../../../redux/hooks';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { InvoiceType } from '../../../../types/InvoiceType';
import type { ServiceType } from '../../../../types/ServiceType';
import ClientServiceModal from './ClientServiceModal';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const useStyle = createStyles(({ css }) => ({
    customTable: css`
        .ant-table {
            &-container {
                .ant-table-body,
                .ant-table-content {
                    scrollbar-width: thin;
                    scrollbar-color: #eaeaea transparent;
                }
            }
        }
    `,
}));



const ClientServices: React.FC = () => {
    const { ledgers } = useAppSelector(state => state.master);
    const [services, setServices] = useState<ServiceType[]>([]);
    const [invoices, setInvoices] = useState<InvoiceType[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [initialValues, setInitialValues] = useState<InvoiceType | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchServices = async () => {
        try {
            const data = await apiRequest<ServiceType[]>({
                method: 'get',
                url: '/get-services',
            });
            setServices(data);
        } catch (error) {
            showError('Failed to fetch services');
            console.error(error);
        }
    };

    const fetchInvoices = async () => {
        try {
            const data = await apiRequest<InvoiceType[]>({
                method: 'get',
                url: '/get-invoices',
            });
            setInvoices(data);
        } catch (error) {
            showError('Failed to fetch invoices');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchServices();
        fetchInvoices();
    }, []);

    const handleEditClick = (invoice: InvoiceType) => {
        setInitialValues(invoice);
        setIsModalVisible(true);
    };

    const hideModal = () => {
        setIsModalVisible(false);
        setInitialValues(null);
    };

    const handleCreate = () => {
        fetchInvoices();
    };

    const handleTableChange: TableProps<InvoiceType>['onChange'] = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
    };

    const columns: TableColumnsType<InvoiceType> = [
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            sorter: (a, b) => (a.invoice_no || '').localeCompare(b.invoice_no || ''),
            sortOrder: sortedInfo.columnKey === 'invoice_no' ? sortedInfo.order : null,
        },
        {
            title: 'Client Name',
            dataIndex: 'ledger_name',
            key: 'ledger_name',
            sorter: (a, b) => a.ledger_name.localeCompare(b.ledger_name),
            sortOrder: sortedInfo.columnKey === 'ledger_name' ? sortedInfo.order : null,
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            render: date => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => dayjs(a.invoice_date).unix() - dayjs(b.invoice_date).unix(),
            sortOrder: sortedInfo.columnKey === 'invoice_date' ? sortedInfo.order : null,
        },
        {
            title: 'Total Amount',
            dataIndex: 'invoice_total',
            key: 'invoice_total',
            sorter: (a, b) => a.invoice_total - b.invoice_total,
            sortOrder: sortedInfo.columnKey === 'invoice_total' ? sortedInfo.order : null,
        },
    ];

    if (currentModule?.right_edit === 'Y') {
        columns.push({
            title: 'Action',
            key: 'action',
            fixed:'right',
            width:150,
            render: (_: any, record: InvoiceType) => (
                <Space>
                    <Button type="primary" onClick={() => handleEditClick(record)}>
                        <EditOutlined /> Edit
                    </Button>
                </Space>
            ),
        });
    }

    return (
        <>
            <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Breadcrumb>
                    <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
                    <Breadcrumb.Item>Invoices</Breadcrumb.Item>
                </Breadcrumb>

                {currentModule?.right_add === 'Y' && (
                    <Button type="primary" onClick={() => setIsModalVisible(true)}>
                        Add Invoice
                    </Button>
                )}
            </Space>

            <div style={{ minHeight: 360, background: colorBgContainer, borderRadius: borderRadiusLG }}>
                <Table
                    tableLayout="auto"
                    className={`${styles.customTable} scrollable-table`}
                    columns={columns}
                    dataSource={invoices.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    rowKey="invoice_id"
                    pagination={false}
                    scroll={{ x: 2100, y: 37 * 16 }}
                    onChange={handleTableChange}
                    bordered
                    locale={{ emptyText: 'No invoices found' }}
                    expandable={{
                        expandedRowRender: record => (
                            <Table
                                columns={[
                                    { title: 'Service Name', dataIndex: 'service_name', key: 'service_name' },
                                    { title: 'Qty', dataIndex: 'qty', key: 'qty' },
                                    { title: 'Rate', dataIndex: 'rate', key: 'rate' },
                                    { title: 'GST %', dataIndex: 'gst', key: 'gst' },
                                    { title: 'GST Amount', dataIndex: 'gst_amount', key: 'gst_amount' },
                                    {
                                        title: 'Total',
                                        key: 'total',
                                        render: (_, service) => ((service.qty || 0) * (service.rate || 0) + (service.gst_amount || 0)).toFixed(2),
                                    },
                                ]}
                                dataSource={record.services.map((s, idx) => ({ ...s, key: idx }))}
                                pagination={false}
                                size="small"
                            />
                        ),
                        rowExpandable: record => record.services.length > 0,
                    }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                                {Math.min(pagination.current * pagination.pageSize, invoices.length)} of {invoices.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={invoices.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>

            <ClientServiceModal
                visible={isModalVisible}
                onCancel={hideModal}
                onCreate={handleCreate}
                services={services}
                initialValues={initialValues}
                ledgers={ledgers}
            />
        </>
    );
};

export default ClientServices;